<script setup lang="ts">
import { computed } from 'vue';
import {
    BBtn, BIcon, BMenu, BListItem, BListItemTitle, BSkeletonLoader,
} from '@/design-system';
import { useConfirmation } from '@/composables';
import { usePageActionsStore } from '@/stores/pageActionsStore';
import { storeToRefs } from 'pinia';
import { VBtn } from 'vuetify/components';

export type PageActionType = {
    key: string;
    label?: string;
    icon?: string;
    color?: string;
    variant?: VBtn['$props']['variant'];
    handler?: () => void | Promise<void>;
    disabled?: boolean;
    rounded?: boolean;
    requiresConfirmation?: boolean;
    isPrimary?: boolean;
    prependIcon?: string;
    appendIcon?: string;
    can?: boolean | (() => boolean);
};

export type PropsType = {
    actions: PageActionType[];
    maxVisible?: number;
    density?: VBtn['$props']['density'];
    rounded?: boolean;
    classWrapper?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    maxVisible: 2,
    density: 'comfortable',
    rounded: true,
});

const emit = defineEmits<{
    action: [action: string];
}>();

const { onConfirm } = useConfirmation();
const { loading } = storeToRefs(usePageActionsStore());

const classWrapper = computed(() => {
    return ['d-flex align-center ga-2 ml-6', props.classWrapper];
});

const allowedActions = computed(() => {
    return props.actions.filter(action => {
        if (action.can === undefined) {
            return true;
        }

        if (typeof action.can === 'boolean') {
            return action.can;
        }

        if (typeof action.can === 'function') {
            return action.can();
        }

        return true;
    });
});

const visibleActions = computed(() => {
    const filtered = allowedActions.value;
    const primaryActions = filtered.filter(a => a.isPrimary === true);

    if (primaryActions.length > 0) {
        return primaryActions.slice(0, props.maxVisible);
    }

    return filtered.slice(0, props.maxVisible);
});

const overflowActions = computed(() => {
    const filtered = allowedActions.value;
    const primaryActions = filtered.filter(a => a.isPrimary === true);

    if (primaryActions.length > 0) {
        const visibleKeys = visibleActions.value.map(a => a.key);

        return filtered.filter(a => !visibleKeys.includes(a.key));
    }

    return filtered.slice(props.maxVisible);
});

const handleAction = async (action: PageActionType) => {
    if (action.requiresConfirmation) {
        if (await onConfirm()) {
            if (action.handler) {
                await action.handler();
            }
            emit('action', action.key);
        }
    } else {
        if (action.handler) {
            await action.handler();
        }
        emit('action', action.key);
    }
};
</script>

<template>
    <BSkeletonLoader
        color="transparent"
        class="page-actions-skeleton"
        :loading="loading"
        type="button"
    >
        <div :class="classWrapper">
            <!-- Primary actions - visible buttons -->
            <BBtn
                v-for="action in visibleActions"
                :key="action.key"
                :color="action.color || 'primary'"
                :variant="action.variant"
                :disabled="action.disabled"
                :icon="!action.label"
                :density="density"
                class="px-4 btn-action"
                :rounded="rounded"
                @click="() => handleAction(action)"
            >
                <div class="d-flex align-center gc-1 text-transform-none">
                    <BIcon
                        v-if="action.prependIcon"
                        size="small"
                    >
                        {{ action.prependIcon }}
                    </BIcon>

                    <template v-if="action.label">
                        {{ action.label }}
                    </template>

                    <BIcon
                        v-if="action.appendIcon"
                        size="small"
                    >
                        {{ action.appendIcon }}
                    </BIcon>
                </div>
            </BBtn>

            <!-- Overflow menu - hidden actions -->
            <BMenu
                v-if="overflowActions.length > 0"
                activator-icon="mdi-dots-vertical"
                :activator-btn-is-icon="false"
                activator-btn-variant="tonal"
                activator-btn-density="comfortable"
                activator-btn-size="default"
                activator-btn-classes="page-actions-btn-more"
                :activator-btn-rounded="true"
            >
                <template #list-items>
                    <BListItem
                        v-for="action in overflowActions"
                        :key="action.key"
                        :disabled="action.disabled"
                        density="compact"
                        @click="() => handleAction(action)"
                    >
                        <BListItemTitle class="d-flex align-center gc-2">
                            <BIcon
                                v-if="action.icon"
                                :color="action.color"
                            >
                                {{ action.icon }}
                            </BIcon>
                            {{ action.label }}
                        </BListItemTitle>
                    </BListItem>
                </template>
            </BMenu>
        </div>
    </BSkeletonLoader>
</template>

<style lang="scss" scoped>
:deep(.page-actions-btn-more) {
    min-width: 50px;
}
.page-actions-skeleton {
    display: flex;
    gap: 8px;
    min-width: 180px;
    margin: 0;
}
:deep(.btn-action.v-btn--density-comfortable),
:deep(.page-actions-btn-more.v-btn--density-comfortable) {
    --v-btn-height: 40px;
}

.page-actions-skeleton :deep(.v-skeleton-loader__button) {
    margin: 0 24px;
    border-radius: 24px;
    height: 32px;
    max-width: 80px;
}
</style>
